import type { ReactNode } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/shared/components/ui/popups/dialog'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

export interface ResponsivePopupProps {
  trigger: ReactNode
  title?: ReactNode
  description?: ReactNode
  children?: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  mobileRoute?: string
  hideHeader?: boolean
  hideCloseButton?: boolean
  className?: string
  footerClassName?: string
  dark?: boolean
  removePadding?: boolean
  footer?: ReactNode
  floatingFooter?: ReactNode
}

export const ResponsivePopup: React.FC<ResponsivePopupProps> = ({
  trigger,
  title,
  description,
  children,
  open,
  onOpenChange,
  mobileRoute,
  hideHeader = false,
  hideCloseButton = false,
  className,
  footerClassName,
  dark = false,
  removePadding = false,
  footer,
  floatingFooter,
}) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  if (isMobile && mobileRoute) {
    return <div onClick={() => navigate(mobileRoute)}>{trigger}</div>
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        hideClose={hideCloseButton}
        className={cn(
          'mx-auto flex max-h-[80vh] w-full flex-col overflow-hidden rounded-[20px]',
          isMobile ? 'max-h-[90vh] w-[90%] max-w-lg sm:w-[85%]' : 'w-full',
          dark && 'border-muted bg-primary text-primary-foreground',
          floatingFooter && 'overflow-visible',
          className,
        )}
      >
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[20px]">
          {!hideHeader && title && (
            <DialogHeader className="shrink-0 px-4 pt-4 sm:px-6 sm:pt-6">
              <DialogTitle className={cn('text-2xl font-bold', dark && 'text-primary-foreground')}>{title}</DialogTitle>
              {description && (
                <DialogDescription className={cn(dark && 'text-primary-foreground/80')}>
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>
          )}
          <div
            className={cn(
              'flex-1 overflow-y-auto scrollbar-hide',
              !removePadding && 'px-4 sm:px-6',
              !removePadding && (hideHeader ? 'pt-4 sm:pt-6' : 'mt-4 pt-2'),
              !removePadding && (footer ? 'pb-0' : 'pb-4 sm:pb-6'),
            )}
          >
            <div className="min-h-full">{children}</div>
          </div>
          {footer && (
            <DialogFooter
              className={cn(
                'shrink-0 border-t border-border bg-background p-4 sm:p-6',
                dark && 'border-primary/20 bg-primary text-primary-foreground',
                footerClassName,
              )}
            >
              {footer}
            </DialogFooter>
          )}
        </div>
        {floatingFooter && <div className="absolute inset-x-0 top-full z-10 mt-5 h-10 w-full">{floatingFooter}</div>}
      </DialogContent>
    </Dialog>
  )
}
